import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "../../lib/api";
import { serviceName } from "../../lib/constants";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { StatCard } from "../../components/shared/StatCard";
import { Rocket, Receipt, Bell, FileText, Copy } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function PortalOverview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/portal/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-muted-foreground text-sm">Loading your operations...</p>;

  return (
    <div className="space-y-8" data-testid="portal-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="portal-kpi-active" label="Active Jobs" value={data.active_missions.length} icon={Rocket} />
        <StatCard testId="portal-kpi-quotes" label="Quote Requests" value={data.quotes.length} icon={FileText} />
        <StatCard testId="portal-kpi-invoices" label="Open Invoices" value={data.open_invoices.length} sub={fmtMoney(data.open_invoices.reduce((a, i) => a + i.total, 0)) + " due"} icon={Receipt} accent={data.open_invoices.length > 0} />
        <StatCard testId="portal-kpi-notifications" label="Notifications" value={data.unread_count} sub="unread" icon={Bell} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border bg-card">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="font-heading font-semibold">Active Missions</h2>
            <Link to="/portal/jobs" className="text-xs text-[var(--cc-accent)] font-semibold">View all</Link>
          </div>
          <div className="divide-y">
            {data.active_missions.length === 0 && <p className="p-6 text-sm text-muted-foreground">No active jobs. <Link to="/quote" className="text-[var(--cc-accent)]">Request a quote</Link> to get started.</p>}
            {data.active_missions.map((m) => (
              <div key={m.id} className="p-5 flex flex-wrap items-center gap-4 justify-between" data-testid={`portal-mission-${m.mission_number}`}>
                <div>
                  <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{m.mission_number}</p>
                  <p className="font-heading font-semibold mt-0.5">{serviceName(m.service_type)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDate(m.scheduled_date)} · {m.scheduled_time}</p>
                </div>
                <StatusBadge status={m.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6" data-testid="portal-referral-card">
            <p className="overline-cc">Refer & Save</p>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Share your code — friends get 10% off their first booking.</p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 rounded-lg border bg-secondary/50 px-3 py-2 font-mono-cc text-sm font-bold tracking-wider" data-testid="portal-referral-code">{data.referral_code || "—"}</code>
              <Button size="icon" variant="outline" className="shrink-0" data-testid="portal-referral-copy" aria-label="Copy referral code"
                onClick={() => { navigator.clipboard.writeText(data.referral_code || ""); toast.success("Code copied — share it anywhere"); }}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {data.open_invoices.length > 0 && (
            <div className="rounded-xl border border-[var(--cc-accent)]/40 bg-card p-6">
              <p className="overline-cc">Payment Due</p>
              <p className="font-heading font-bold text-2xl mt-2">{fmtMoney(data.open_invoices.reduce((a, i) => a + i.total, 0))}</p>
              <p className="text-xs text-muted-foreground mt-1">{data.open_invoices.length} open invoice{data.open_invoices.length > 1 ? "s" : ""}</p>
              <Link to="/portal/invoices">
                <Button data-testid="portal-pay-now-button" className="w-full mt-4 rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">Pay Now</Button>
              </Link>
            </div>
          )}
          <div className="rounded-xl border bg-card">
            <div className="px-6 py-4 border-b"><h2 className="font-heading font-semibold">Notifications</h2></div>
            <div className="divide-y max-h-72 overflow-y-auto">
              {data.notifications.length === 0 && <p className="p-5 text-sm text-muted-foreground">All quiet.</p>}
              {data.notifications.map((n) => (
                <div key={n.id} className="p-4">
                  <div className="flex items-center gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[var(--cc-accent)] shrink-0" />}
                    <p className="text-sm font-semibold">{n.title}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{n.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
